import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useLocalSearchParams, useRouter } from "expo-router";
import { z } from "zod";

import { FormField } from "~/components/composed/FormField";
import {
  AppLink,
  AppText,
  Button,
  ButtonText,
  Card,
  PasswordField,
  Spinner,
  Stack,
} from "~/components/ui";
import { AuthScreen } from "~/components/composed/AuthScreen";
import { useAuth } from "~/hooks/useAuth";
import { usePasswordValidation } from "~/hooks/usePasswordValidation";
import { passwordResetSchema } from "~/utils/form-schemas";

type ResetValues = z.infer<typeof passwordResetSchema>;

export default function ResetPasswordScreen() {
  const params = useLocalSearchParams<{ token?: string }>();
  const router = useRouter();
  const { resetPassword, resetPasswordPending } = useAuth();
  // The emailed link carries a one-time token; there is nothing to prepare.
  const isPreparing = false;
  const preparationError = params.token
    ? null
    : "This reset link is invalid or expired.";
  const form = useForm<ResetValues>({
    resolver: zodResolver(passwordResetSchema),
    mode: "onBlur",
    reValidateMode: "onChange",
    defaultValues: { password: "", confirmPassword: "" },
  });
  const validation = usePasswordValidation(form.watch("password"));

  const submit = form.handleSubmit(async ({ password }) => {
    try {
      await resetPassword({ token: params.token ?? "", password });
      router.replace("/login");
    } catch (error) {
      form.setError("root", {
        message:
          error instanceof Error ? error.message : "Unable to reset password.",
      });
    }
  });

  return (
    <AuthScreen title="Reset password">
      {isPreparing ? (
        <Spinner />
      ) : preparationError ? (
        <Card>
          <AppText tone="danger">{preparationError}</AppText>
        </Card>
      ) : (
        <Stack>
          {form.formState.errors.root?.message ? (
            <AppText tone="danger">
              {form.formState.errors.root.message}
            </AppText>
          ) : null}
          <FormField
            control={form.control}
            name="password"
            label="New password"
          >
            {(field) => (
              <PasswordField
                {...field}
                value={String(field.value ?? "")}
                autoComplete="new-password"
                accessibilityLabel="New password"
              />
            )}
          </FormField>
          <FormField
            control={form.control}
            name="confirmPassword"
            label="Confirm new password"
          >
            {(field) => (
              <PasswordField
                {...field}
                value={String(field.value ?? "")}
                autoComplete="new-password"
                accessibilityLabel="Confirm new password"
              />
            )}
          </FormField>
          {validation ? (
            <Card compact>
              <AppText size="xs">
                Password strength: {validation.strength}
              </AppText>
              {validation.errors.map((message) => (
                <AppText key={message} tone="danger" size="xs">
                  - {message}
                </AppText>
              ))}
            </Card>
          ) : null}
          <Button disabled={resetPasswordPending} onPress={() => void submit()}>
            <ButtonText>
              {resetPasswordPending ? "Saving..." : "Set new password"}
            </ButtonText>
          </Button>
        </Stack>
      )}
      <AppLink href="/login">Back to sign in</AppLink>
    </AuthScreen>
  );
}
